import { FC, useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
} from "@mui/material";
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { Toolbar } from "../components/FooterToolbar";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import GPALineChart from "../components/GPALineChart";
import CommentMessages from "../components/CommentMessages";
import {
  descriptionText,
  commentGPA,
} from "../common/Comment";
import {
  gpaRequestType,
  gpaResponseType,
} from "../common/type";
import {
  useAuth0Token,
  getGpaScore,
} from "../common/http-requests";
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  useUserId,
  useUserNickname,
  useUserStudentNumber,
  useLanguage,
} from "../hooks/useStoreGetters";

const BAR_CHART_SIZE: number[] = [
  1000, 320
];

// Material-UIのスタイル設定
const useStyles = makeStyles({
  chartContainer: {
    position: 'relative',
    marginTop: '20px',
    padding: '20px',
    overflow: 'hidden'
  }
});

const LINE_CHART_SIZE: number[] = [
  1200, 600
];

/** @AIからの分析は対応不要。
const StyledPaperStyle = {
  offsetY: 22,
  offsetX: -10,
  bgColor: "#226DAE",
}
  */

const GPAPage: FC = () => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(true);
  const { getToken } = useAuth0Token();
  const userId = useUserId();
  const userNickname = useUserNickname();
  const userStudentNumber = useUserStudentNumber();
  const language = useLanguage();
  const classes = useStyles();
  const { isDesktop } = useCustomMediaQuery();
  const [commentMessage, setCommentMessage] = useState<string>('');

  const [gpaScoreData, setGpaScoreData] = useState<gpaResponseType>({
    scoreList: [],
    seriesNameList: [],
  });

  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });

  // clickawayした際、api取得失敗時のアラートを行わない。
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  // 以下は、記録一覧画面のコードを流用した。
  // Phase1と異なり、画面遷移が必要ではないため、location（uselocation）は使用しない。
  // React18(2022年)より、strictMode（javascriptのコードを通常より厳しくエラーチェックできるモード）において
  // オフスクリーンから再度表示されることで、useEffectが２回発火（キック）される仕様変更となっため、1回に制限するよう変更した。
  // また、以下のコードの})();の();は、定義した関数を即時実行している。
  // 最後にreturn文にて定義された関数は、クリーンアップ関数であり、リスナー、オブザーバー等のオブジェクトを設定した場合、
  // レンダリング時に毎回インスタンスを作成されるため、メモリーリーク及びメモリーの圧迫を抑止するための関数。
  // useEffectの依存配列（第2引数）は、通常、空リストもしくは、具体的な変数のリストを登録するのが一般的であり、
  // 定義しない場合は、FCのstate, propsのうちいずれかが更新される度に実行されてしまうため、通常、定義しないことはない。
  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getGpaScoreData(userId, userStudentNumber);
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);

  const getGpaScoreData = async (
    login_user_id: number, // userResponseType.id の値
    student_number: string,
  ) => {
    setIsSending(true);
    try {
      const requestParams: gpaRequestType = {
        login_user_id,
        student_number,
      };
      const data: gpaResponseType = await getGpaScore(
        await getToken(),
        requestParams
      );
      setGpaScoreData(data);

      const comment = commentGPA(login_user_id, userNickname, t, data);
      setCommentMessage(comment);
    } catch (e) {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const SummaryMessage = styled(Box)(() => ({
    maxWidth: `${BAR_CHART_SIZE[0]}px`,
    margin: isDesktop ? '3.5rem auto 1rem' : '7.5rem auto 1rem',
    textAlign: 'center',
  }));

  return (
    <Container maxWidth="xl">
      {isSending ?
        <CircularProgressMolecule displayLabel={t("msg.loading")} /> :
        (
          <>
            <VisualizedMenus />
            {feedbackInfo.open &&
              <FeedbackBar
                feedbackInfo={feedbackInfo}
                handleClose={handleClose}
              ></FeedbackBar>
            }
            <SummaryMessage>{descriptionText(userNickname, language, t, "gpa")}</SummaryMessage>
            <CommentMessages message={commentMessage} />
            <Paper className={classes.chartContainer}>
              {/** <CustomTooltip /> @グラフ上部に「自分」の平均値を表示するコンポーネント。 */}
              <GPALineChart
                chartSize={LINE_CHART_SIZE}
                data={gpaScoreData}
              />
            </Paper>
          </>
        )}
      <Toolbar />
    </Container>
  );
};

export default GPAPage;
